import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { getPaymentPlanDisplayName } from "../utils/eftUtils";
import axiosInstance from "../config/axios";
import { DEFAULT_TAX_RATE } from "../constants/tax";
import { useRole } from "../context/RoleContext";
import { roundTo2, roundTo2Str } from "../utils/numberUtils";
import {
  OFFICE_EXPENSE_OPTIONS,
  computeOfficeExpenses,
  emptyOfficeExpenses,
  normalizeOfficeExpenses,
  officeExpensesTaxRatePercent,
  resolveOfficeExpensesTaxRate,
} from "../utils/officeExpenses";

const feePlans = [
  { label: "Referral Broker", value: "referralBroker" },
  { label: "Plan 250", value: "plan250" },
  { label: "Plan 500", value: "plan500" },
  { label: "Plan 90/10", value: "plan9010" },
  { label: "Plan 95/5", value: "plan955" },
  { label: "Plan 85/15", value: "plan8515" },
  { label: "Plan 50/50", value: "plan5050" },
  { label: "Plan 80/20", value: "plan8020" },
  { label: "Plan 150", value: "plan150" },
  { label: "Flexible Plan", value: "flexible" },
];

const initialAgent = {
  agentId: "",
  agentName: "",
  classification: "",
  ytdCommission: "Yes",
  awardAmount: "",
  percentage: 100,
  amount: "",
  feeInfo: "plan500",
  feesDeducted: "",
  tax: "",
  taxOverridden: false,
  taxOnFees: "",
  total: "",
  totalFees: "",
  netCommission: "",
  lead: "No",
  alreadyAdded: false,
  transactionType: "", // "LISTING" or "SELLING" for AGENT DOUBLE ENDER
  officeExpensesEnabled: false,
  officeExpensesTaxRate: "",
  officeExpenses: emptyOfficeExpenses(),
  officeExpensesTotal: "",
};

const initialSaleClosing = {
  sellPrice: "",
  closingDate: "",
  tradeNumber: "",
  status: "Active",
  ar: "",
};

/** Sum of % across agents already saved to the list (listing / co-op split). */
function sumAgentPercentages(agentRows) {
  return roundTo2(
    agentRows.reduce((acc, a) => acc + (parseFloat(a.percentage) || 0), 0),
  );
}

/** Remaining % for the next empty row: 100 − sum(all added agents). */
function remainingPercentAfterAgents(agentRows) {
  return Math.max(0, roundTo2(100 - sumAgentPercentages(agentRows)));
}

/** Which commission side a row counts against (listing vs selling). */
function getCommissionSide(agentRow) {
  const classification = String(agentRow?.classification || "").toUpperCase();
  const transactionType = String(agentRow?.transactionType || "").toUpperCase();
  if (classification === "AGENT DOUBLE ENDER") {
    if (transactionType === "LISTING") return "listing";
    if (transactionType === "SELLING") return "selling";
    return "";
  }
  if (classification === "LISTING SIDE") return "listing";
  if (
    classification === "SELLING SIDE" ||
    classification === "CO-OPERATING SIDE"
  ) {
    return "selling";
  }
  return "";
}

function sumAmountsForCommissionSide(agentRows, side, excludeIndex = null) {
  return roundTo2(
    (agentRows || []).reduce((sum, row, idx) => {
      if (excludeIndex != null && idx === excludeIndex) return sum;
      if (getCommissionSide(row) !== side) return sum;
      return sum + (parseFloat(row.amount) || 0);
    }, 0),
  );
}

/**
 * Listing amounts must not exceed Commission Income listing amount;
 * selling amounts must not exceed Commission Income selling amount.
 */
function validateAgainstCommissionIncome(
  agentRow,
  agentRows,
  commissionData,
  excludeIndex = null,
) {
  const side = getCommissionSide(agentRow);
  if (!side) return { ok: true };

  const cap =
    side === "listing"
      ? roundTo2(parseFloat(commissionData?.listingAmount) || 0)
      : roundTo2(parseFloat(commissionData?.sellingAmount) || 0);
  const thisAmount = roundTo2(parseFloat(agentRow.amount) || 0);
  const already = sumAmountsForCommissionSide(agentRows, side, excludeIndex);
  const nextTotal = roundTo2(already + thisAmount);

  if (cap <= 0) {
    return {
      ok: false,
      message:
        side === "listing"
          ? "Commission Income has no listing amount to allocate."
          : "Commission Income has no selling amount to allocate.",
    };
  }

  if (nextTotal > cap + 0.009) {
    const label = side === "listing" ? "listing" : "selling";
    return {
      ok: false,
      message: `Total ${label} commission ($${nextTotal.toFixed(
        2,
      )}) would exceed Commission Income ${label} amount ($${cap.toFixed(2)}). Remaining: $${Math.max(
        0,
        roundTo2(cap - already),
      ).toFixed(2)}.`,
    };
  }

  return { ok: true };
}

/** Default ADE listing % from Commission Income listing/selling amounts. */
function defaultAdeListingPercent(commissionData) {
  const listing = parseFloat(commissionData?.listingAmount) || 0;
  const selling = parseFloat(commissionData?.sellingAmount) || 0;
  const pool = roundTo2(listing + selling);
  if (pool <= 0) return 50;
  return roundTo2((listing / pool) * 100);
}

/** Total commission pool (listing + selling) for AGENT DOUBLE ENDER. */
function getAdeCommissionPool(commissionData) {
  const listing = parseFloat(commissionData?.listingAmount) || 0;
  const selling = parseFloat(commissionData?.sellingAmount) || 0;
  return roundTo2(listing + selling);
}

/**
 * ADE uses full pool as Award Amount; Percentage on each side allocates the share.
 * Migrates older rows that stored half-pool as award with 100%.
 */
function normalizeAdeAwardAndPercent(agentObj, commissionData) {
  const pool = getAdeCommissionPool(commissionData);
  if (pool <= 0) {
    return {
      ...agentObj,
      awardAmount: "0.00",
    };
  }
  const currentAward = parseFloat(agentObj.awardAmount) || 0;
  const pct = parseFloat(agentObj.percentage);
  const safePct = Number.isFinite(pct) ? pct : 100;
  const currentAmount = roundTo2((currentAward * safePct) / 100);

  if (Math.abs(currentAward - pool) < 0.02) {
    return {
      ...agentObj,
      awardAmount: pool.toFixed(2),
    };
  }

  // Legacy: award was listing/selling side amount — convert to pool × %
  if (currentAward > 0) {
    const newPct = roundTo2((currentAmount / pool) * 100);
    return {
      ...agentObj,
      awardAmount: pool.toFixed(2),
      percentage: String(newPct > 0 ? newPct : safePct),
    };
  }

  return {
    ...agentObj,
    awardAmount: pool.toFixed(2),
  };
}

const AgentCommissionInfo = ({
  goToPreviousSection,
  agent,
  setAgent,
  agents,
  setAgents,
  editingIndex,
  setEditingIndex,
  showDeleteModal,
  setShowDeleteModal,
  deletingIndex,
  setDeletingIndex,
  showBuyerRebateModal,
  setShowBuyerRebateModal,
  buyerRebateIncluded,
  setBuyerRebateIncluded,
  buyerRebateAmount,
  setBuyerRebateAmount,
  pendingAgent,
  setPendingAgent,
  validationErrors,
  setValidationErrors,
  onSaveTrade,
  classification,
  commissionData,
  selectedListing,
  isEditingExistingTrade = false,
  isEditMode = false,
  taxRate = DEFAULT_TAX_RATE,
}) => {
  const { isAgent, appUser } = useRole();
  const [agentList, setAgentList] = useState([]);
  const [isReferral, setIsReferral] = useState(false);
  /** Fee plan to restore when user unchecks Referral (new entry flow). */
  const feeInfoBeforeReferralRef = useRef(null);
  const [saleClosingRows, setSaleClosingRows] = useState([]);
  const [saleClosing, setSaleClosing] = useState(initialSaleClosing);
  const [isSaving, setIsSaving] = useState(false);
  const [isTradeSaved, setIsTradeSaved] = useState(false);
  const [selectedListingAgentIndex, setSelectedListingAgentIndex] = useState(0);
  // For AGENT DOUBLE ENDER: when adding an agent who has neither LISTING nor SELLING, we add both at once
  const [pendingAgentsToAdd, setPendingAgentsToAdd] = useState([]);

  // Fetch agent list for dropdown (agents only see themselves)
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axiosInstance.get("/agents");
        const list = Array.isArray(response.data) ? response.data : [];
        if (!isAgent || !appUser) {
          setAgentList(list);
          return;
        }
        const emp = String(appUser.employeeNo || "").trim();
        const agentId = String(appUser.agentId || "").trim();
        const filtered = list.filter((a) => {
          const no = String(a.employeeNo || "").trim();
          const id = String(a._id || "").trim();
          return (
            (emp && no === emp) ||
            (agentId && (id === agentId || no === agentId))
          );
        });
        if (filtered.length > 0) {
          setAgentList(filtered);
          return;
        }
        const nameParts = String(appUser.displayName || "")
          .trim()
          .split(/\s+/);
        setAgentList([
          {
            _id: agentId || emp || "self",
            employeeNo: emp || agentId,
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" ") || "",
          },
        ]);
      } catch (error) {
        setAgentList([]);
      }
    };
    fetchAgents();
  }, [isAgent, appUser]);

  // Prefill the signed-in agent on Agent Info
  useEffect(() => {
    if (!isAgent || !appUser) return;
    if (String(agent.agentId || "").trim()) return;
    const emp = String(appUser.employeeNo || appUser.agentId || "").trim();
    if (!emp) return;
    const match =
      agentList.find((a) => String(a.employeeNo || "").trim() === emp) ||
      agentList[0];
    if (!match) return;
    setAgent((prev) => ({
      ...prev,
      agentId: String(match.employeeNo || emp),
      agentName:
        `${match.firstName || ""} ${match.lastName || ""}`.trim() ||
        appUser.displayName ||
        "",
    }));
  }, [isAgent, appUser, agentList, agent.agentId, setAgent]);

  // Reset selected agent index when listing changes
  useEffect(() => {
    setSelectedListingAgentIndex(0);
  }, [selectedListing]);

  const listingAgentsSource = useMemo(() => {
    const rows = Array.isArray(selectedListing?.agents)
      ? selectedListing.agents.filter(
          (row) =>
            row &&
            (String(row.agentNo || "").trim() ||
              String(row.firstName || "").trim() ||
              String(row.lastName || "").trim()),
        )
      : [];
    let source = rows;
    if (rows.length === 0) {
      const emp = String(selectedListing?.agent?.employeeNo || "").trim();
      if (emp) {
        const match = (agentList || []).find(
          (a) => String(a.employeeNo || "").trim() === emp,
        );
        if (match) {
          source = [
            {
              agentNo: String(match.employeeNo),
              firstName: match.firstName || "",
              lastName: match.lastName || "",
            },
          ];
        } else {
          source = [{ agentNo: emp, firstName: "", lastName: "" }];
        }
      }
    }
    if (!isAgent || !appUser) return source;
    const selfEmp = String(appUser.employeeNo || appUser.agentId || "").trim();
    if (!selfEmp) return source;
    return source.filter(
      (row) => String(row.agentNo || "").trim() === selfEmp,
    );
  }, [selectedListing, agentList, isAgent, appUser]);

  // Auto-calculate amount when awardAmount or percentage changes
  useEffect(() => {
    setAgent((prev) => {
      const isAde =
        classification === "AGENT DOUBLE ENDER" ||
        prev.classification === "AGENT DOUBLE ENDER";
      // Amount was typed by user (ADE only) — keep it; only refresh fees
      if (isAde && prev.preferEnteredAmount) {
        return calculateFees({ ...prev, preferEnteredAmount: true });
      }
      const award = parseFloat(prev.awardAmount) || 0;
      const perc = parseFloat(prev.percentage) || 0;
      const amount = roundTo2Str(roundTo2((award * perc) / 100));
      return calculateFees({
        ...prev,
        amount,
        preferEnteredAmount: false,
      });
    });
    // eslint-disable-next-line
  }, [agent.awardAmount, agent.percentage, agent.feeInfo, agent.feesDeducted]);

  // Recalculate current agent and all added agents when tax rate changes (e.g. Set Tax Rate applied in Commission Income)
  useEffect(() => {
    setAgent((prev) => calculateFees(prev));
    setAgents((prevList) => prevList.map((a) => calculateFees(a)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxRate]);

  // Recalculate award amount when transactionType changes for AGENT DOUBLE ENDER
  useEffect(() => {
    if (editingIndex !== null) return;
    if (classification === "AGENT DOUBLE ENDER" && commissionData) {
      // Automatically determine transaction type if not set
      let transactionType = agent.transactionType;
      if (!transactionType && agent.agentId) {
        // Check what transaction types already exist for this agent
        const hasListing = agents.some(
          (a) =>
            a.agentId === agent.agentId &&
            a.classification === "AGENT DOUBLE ENDER" &&
            a.transactionType === "LISTING",
        );
        const hasSelling = agents.some(
          (a) =>
            a.agentId === agent.agentId &&
            a.classification === "AGENT DOUBLE ENDER" &&
            a.transactionType === "SELLING",
        );

        // Automatically set transaction type - Listing first, then Selling
        if (!hasListing) {
          transactionType = "LISTING";
        } else if (!hasSelling) {
          transactionType = "SELLING";
        } else {
          // Both already added, default to Listing
          transactionType = "LISTING";
        }
      } else if (!transactionType) {
        // No agent ID yet, default to Listing
        transactionType = "LISTING";
      }

      const normalized = normalizeAdeAwardAndPercent(
        {
          ...agent,
          transactionType,
        },
        commissionData,
      );

      // Suggest %: first row from commission ratio; after that = 100 − sum already posted
      let nextPercentage = normalized.percentage;
      const currentPct = parseFloat(normalized.percentage);
      if (currentPct === 100 || !Number.isFinite(currentPct)) {
        if (agents.length >= 1) {
          nextPercentage = String(remainingPercentAfterAgents(agents));
        } else if (transactionType === "LISTING") {
          nextPercentage = String(defaultAdeListingPercent(commissionData));
        } else {
          nextPercentage = String(
            roundTo2(100 - defaultAdeListingPercent(commissionData)),
          );
        }
      }

      const updatedAgent = {
        ...normalized,
        transactionType,
        percentage: nextPercentage,
      };

      if (
        agent.awardAmount !== updatedAgent.awardAmount ||
        agent.transactionType !== transactionType ||
        String(agent.percentage) !== String(updatedAgent.percentage)
      ) {
        setAgent(calculateFees(updatedAgent));
      }
    }
  }, [
    agent.transactionType,
    agent.agentId,
    classification,
    commissionData,
    agents,
    editingIndex,
  ]);

  // Auto-calculate next row % = 100 − sum(all agents in list)
  useEffect(() => {
    if (editingIndex !== null) return;
    if (
      (agent.classification === "LISTING SIDE" ||
        agent.classification === "CO-OPERATING SIDE" ||
        agent.classification === "AGENT DOUBLE ENDER") &&
      agents.length >= 1
    ) {
      const remainingPercentage = remainingPercentAfterAgents(agents);
      const current = parseFloat(agent.percentage);
      // Only fill when still at default 100% so manual edits are not overwritten
      if (current === 100) {
        setAgent((prev) => ({
          ...prev,
          percentage: String(remainingPercentage),
        }));
      }
    }
  }, [agents, agent.classification, agent.percentage, editingIndex]);

  // Calculate award amount based on classification and commission data
  useEffect(() => {
    if (classification && commissionData) {
      // Skip AGENT DOUBLE ENDER here - handled by the transactionType-specific effect
      if (classification === "AGENT DOUBLE ENDER") {
        return;
      }

      let calculatedAwardAmount = "0.00";

      if (classification === "LISTING SIDE") {
        const listingAmount = parseFloat(commissionData.listingAmount) || 0;
        calculatedAwardAmount = listingAmount.toFixed(2);
      } else if (classification === "SELLING SIDE") {
        const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
        calculatedAwardAmount = sellingAmount.toFixed(2);
      } else if (classification === "CO-OPERATING SIDE") {
        const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
        calculatedAwardAmount = sellingAmount.toFixed(2);
      } else {
        calculatedAwardAmount = "0.00";
      }

      // Only update if award amount is empty or different
      if (
        !agent.awardAmount ||
        agent.awardAmount === "0.00" ||
        agent.awardAmount !== calculatedAwardAmount
      ) {
        const updatedAgent = {
          ...agent,
          awardAmount: calculatedAwardAmount,
        };
        setAgent(calculateFees(updatedAgent));
      }
    }
  }, [classification, commissionData, agent.awardAmount]);

  // Set classification from KeyInfoForm when available
  useEffect(() => {
    if (classification) {
      setAgent((prev) => ({
        ...prev,
        classification: classification,
        // Don't auto-set transactionType here - let the useEffect handle it based on existing agents
        transactionType:
          classification !== "AGENT DOUBLE ENDER" ? "" : prev.transactionType,
      }));

      // Clear agent information when switching to CO-OPERATING SIDE
      if (classification === "CO-OPERATING SIDE") {
        setAgent((prev) => ({
          ...prev,
          agentId: "",
          agentName: "",
        }));
        // Clear validation errors when switching classification
        setValidationErrors((prev) => ({
          ...prev,
          agentId: "",
          agentName: "",
        }));
      }
    }
  }, [classification]);

  // Referral agents: no desk fees — plan must be Referral Broker and fees deducted 0
  useEffect(() => {
    if (agent.classification !== "Referral") return;
    setIsReferral(true);
    setAgent((prev) => {
      if (prev.classification !== "Referral") return prev;
      const fd = parseFloat(String(prev.feesDeducted).replace(/,/g, "")) || 0;
      if (prev.feeInfo === "referralBroker" && fd === 0) return prev;
      return calculateFees({
        ...prev,
        feeInfo: "referralBroker",
        feesDeducted: "0.00",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.classification]);

  // Auto-populate agent information from selected listing (for LISTING SIDE and AGENT DOUBLE ENDER)
  useEffect(() => {
    console.log("selectedListing in AgentCommissionInfo:", selectedListing);
    console.log("selectedListing type:", typeof selectedListing);
    console.log(
      "selectedListing keys:",
      selectedListing ? Object.keys(selectedListing) : "null",
    );
    console.log("isEditingExistingTrade:", isEditingExistingTrade);
    console.log("classification:", classification);

    // When editing a specific row (editingIndex set), don't overwrite - user is modifying that row
    if (editingIndex !== null) {
      return;
    }

    // Auto-populate if classification is LISTING SIDE or AGENT DOUBLE ENDER
    if (
      classification !== "LISTING SIDE" &&
      classification !== "AGENT DOUBLE ENDER"
    ) {
      console.log(
        "Classification is not LISTING SIDE or AGENT DOUBLE ENDER - not auto-populating agent information",
      );
      return;
    }

    if (selectedListing) {
      console.log("selectedListing.agent:", selectedListing.agent);
      console.log("selectedListing.agents:", selectedListing.agents);

      let listingAgent = null;

      // Check if agent is in the agents array first (this is the primary location)
      if (selectedListing.agents && selectedListing.agents.length > 0) {
        console.log(
          "Found agent in selectedListing.agents:",
          selectedListing.agents,
        );
        // Get available agents (not already added)
        const availableAgents = getAvailableListingAgents();
        if (availableAgents.length > 0) {
          // Use the selected agent index from available agents, defaulting to 0
          const agentIndex =
            selectedListingAgentIndex < availableAgents.length
              ? selectedListingAgentIndex
              : 0;
          listingAgent = availableAgents[agentIndex];
          console.log(
            "Using available agent at index:",
            agentIndex,
            listingAgent,
          );
        } else {
          // No available agents - show the last available agent but mark as already added
          console.log(
            "No available agents, showing last agent as already added",
          );
          if (selectedListing.agents && selectedListing.agents.length > 0) {
            const lastAgent =
              selectedListing.agents[selectedListing.agents.length - 1];
            const agentId = lastAgent.agentNo?.toString() || "";
            const agentName = `${lastAgent.firstName || ""} ${
              lastAgent.lastName || ""
            }`.trim();

            if (agentId && agentName) {
              setAgent((prev) => ({
                ...prev,
                agentId: agentId,
                agentName: agentName,
                // Mark this agent as already added by setting a flag
                alreadyAdded: true,
              }));
            }
          }
          return; // Exit early to prevent further processing
        }
      }
      // Check if agent is in the agent property (fallback)
      else if (selectedListing.agent) {
        console.log(
          "Found agent in selectedListing.agent:",
          selectedListing.agent,
        );
        listingAgent = selectedListing.agent;
      }

      if (listingAgent) {
        console.log("listingAgent to use:", listingAgent);
        console.log("listingAgent keys:", Object.keys(listingAgent));

        // Handle both data structures
        let agentId = "";
        let agentName = "";

        if (selectedListing.agents && selectedListing.agents.length > 0) {
          // Using agents array structure
          agentId = listingAgent.agentNo?.toString() || "";
          agentName = `${listingAgent.firstName || ""} ${
            listingAgent.lastName || ""
          }`.trim();
        } else if (selectedListing.agent) {
          // Using singular agent structure
          agentId = listingAgent.employeeNo?.toString() || "";
          // For singular agent, we need to get the name from the agents array if available
          if (selectedListing.agents && selectedListing.agents.length > 0) {
            const agentFromArray = selectedListing.agents[0];
            agentName = `${agentFromArray.firstName || ""} ${
              agentFromArray.lastName || ""
            }`.trim();
          } else {
            agentName = ""; // No name available in singular agent structure
          }
        }

        console.log("Calculated agentId:", agentId);
        console.log("Calculated agentName:", agentName);

        if (agentId && agentName) {
          setAgent((prev) => ({
            ...prev,
            agentId: agentId,
            agentName: agentName,
          }));
          // Clear validation errors when agent is auto-populated
          setValidationErrors((prev) => ({
            ...prev,
            agentId: "",
            agentName: "",
          }));
        } else {
          console.log("Agent ID or name is missing, not setting agent data");
        }
      } else {
        console.log("No agent found in selectedListing");
      }
    }
  }, [
    selectedListing,
    editingIndex,
    classification,
    selectedListingAgentIndex,
    agents, // Add agents to dependency array so auto-population re-runs when agents are added
  ]);

  const validateForm = () => {
    const errors = {};
    if (!String(agent.agentId || "").trim()) {
      errors.agentId = "Agent ID is required";
    }
    if (!String(agent.agentName || "").trim()) {
      errors.agentName = "Agent Name is required";
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the validation errors.");
    }
    return Object.keys(errors).length === 0;
  };

  /** Block add/update when listing/selling amounts would exceed Commission Income caps. */
  const validateCommissionCaps = (agentRow) => {
    if (isReferral || agentRow?.classification === "Referral") return true;
    const check = validateAgainstCommissionIncome(
      agentRow,
      agents,
      commissionData,
      editingIndex,
    );
    if (!check.ok) {
      toast.error(check.message);
      return false;
    }
    return true;
  };

  const handleAgentInputChange = (e) => {
    const { name, value } = e.target;

    // Amount → Percentage only for AGENT DOUBLE ENDER
    if (
      name === "amount" &&
      (classification === "AGENT DOUBLE ENDER" ||
        agent.classification === "AGENT DOUBLE ENDER")
    ) {
      const award =
        parseFloat(String(agent.awardAmount || "").replace(/,/g, "")) || 0;
      const rawAmt = String(value).replace(/,/g, "").trim();
      const amt = rawAmt === "" ? 0 : parseFloat(rawAmt) || 0;
      const pct = award > 0 ? roundTo2((amt / award) * 100) : 0;
      setAgent(
        calculateFees({
          ...agent,
          amount: value,
          percentage: pct.toFixed(2),
          preferEnteredAmount: true,
        }),
      );
      if (validationErrors.amount || validationErrors.percentage) {
        setValidationErrors((prev) => ({
          ...prev,
          amount: "",
          percentage: "",
        }));
      }
      return;
    }

    // Percentage (or other fields) → Amount from Award × %
    let updated = {
      ...agent,
      [name]: value,
      preferEnteredAmount:
        name === "percentage" ? false : agent.preferEnteredAmount,
    };
    setAgent(calculateFees(updated));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAgentSelect = async (e) => {
    const selectedId = e.target.value;
    const selected = agentList.find((a) => a._id === selectedId);
    if (selected) {
      const employeeNo =
        selected.employeeNo != null ? String(selected.employeeNo) : "";
      const agentName = `${selected.firstName} ${selected.lastName}`.trim();
      setAgent((prev) => ({
        ...prev,
        agentId: selected.employeeNo,
        agentName,
      }));
      setValidationErrors((prev) => ({
        ...prev,
        agentId: "",
        agentName: "",
      }));

      // Load outstanding Agent Expense amounts into Office Expenses
      const key = employeeNo || selectedId;
      if (key) {
        try {
          const res = await axiosInstance.get(
            `/agent-expenses/balances/${encodeURIComponent(key)}`,
          );
          const suggested = res.data?.suggestedAmounts || {};
          const expenses = emptyOfficeExpenses();
          let any = false;
          for (const opt of OFFICE_EXPENSE_OPTIONS) {
            const raw = suggested[opt.key];
            const amt =
              parseFloat(String(raw ?? "").replace(/,/g, "")) || 0;
            if (amt > 0) {
              expenses[opt.key] = {
                selected: true,
                amount: String(amt),
              };
              any = true;
            }
          }
          if (any) {
            setAgent((prev) =>
              calculateFees({
                ...prev,
                agentId: selected.employeeNo,
                agentName,
                officeExpensesEnabled: true,
                officeExpenses: expenses,
              }),
            );
          }
        } catch (err) {
          console.warn(
            "Could not load agent expense balances for office expenses:",
            err,
          );
        }
      }
    } else {
      setAgent((prev) => ({ ...prev, agentId: "", agentName: "" }));
    }
  };

  const handleListingAgentSelect = (e) => {
    const selectedIndex = parseInt(e.target.value);
    setSelectedListingAgentIndex(selectedIndex);

    const availableAgents = getAvailableListingAgents();
    if (availableAgents[selectedIndex]) {
      const selectedAgent = availableAgents[selectedIndex];
      const agentId = selectedAgent.agentNo?.toString() || "";
      const agentName = `${selectedAgent.firstName || ""} ${
        selectedAgent.lastName || ""
      }`.trim();

      setAgent((prev) => ({
        ...prev,
        agentId: agentId,
        agentName: agentName,
      }));

      // Clear validation errors
      setValidationErrors((prev) => ({
        ...prev,
        agentId: "",
        agentName: "",
      }));
    }
  };

  // Get available agents (not already added to the table)
  const getAvailableListingAgents = () => {
    if (!listingAgentsSource.length) return [];

    // For AGENT DOUBLE ENDER: only filter out agents who have BOTH listing and selling
    if (classification === "AGENT DOUBLE ENDER") {
      return listingAgentsSource.filter((listingAgent) => {
        const agentId = String(listingAgent.agentNo ?? "").trim();
        if (!agentId) return true;
        const hasListing = agents.some(
          (a) =>
            String(a.agentId || "").trim() === agentId &&
            a.classification === "AGENT DOUBLE ENDER" &&
            a.transactionType === "LISTING",
        );
        const hasSelling = agents.some(
          (a) =>
            String(a.agentId || "").trim() === agentId &&
            a.classification === "AGENT DOUBLE ENDER" &&
            a.transactionType === "SELLING",
        );
        return !(hasListing && hasSelling);
      });
    }

    // For other classifications: filter out any agent already in the table
    const existingAgentIds = agents.map((a) => String(a.agentId || "").trim());
    return listingAgentsSource.filter((listingAgent) => {
      const agentId = listingAgent.agentNo?.toString() || "";
      return agentId && !existingAgentIds.includes(agentId);
    });
  };

  // Get available CO-OPERATING SIDE agents (not already added to the table)
  const getAvailableCoOperatingAgents = () => {
    // Get agent IDs that are already in the table for CO-OPERATING SIDE
    const existingCoOperatingAgentIds = agents
      .filter((a) => a.classification === "CO-OPERATING SIDE")
      .map((a) => a.agentId);

    // Filter out agents that are already added
    return agentList.filter((agentItem) => {
      const agentId = agentItem.employeeNo?.toString() || "";
      return !existingCoOperatingAgentIds.includes(agentId);
    });
  };

  const getAvailableListingSideOfficeAgents = () => {
    const existingListingAgentIds = agents
      .filter((a) => a.classification === "LISTING SIDE")
      .map((a) => String(a.agentId || "").trim());
    return agentList.filter((agentItem) => {
      const agentId = agentItem.employeeNo?.toString() || "";
      return agentId && !existingListingAgentIds.includes(agentId);
    });
  };

  // Check if agent is already added (with transaction type for AGENT DOUBLE ENDER)
  // excludeIndex: when editing a row, ignore that row so Update stays enabled
  const isAgentAlreadyAdded = (
    agentId,
    agentClassification,
    transactionType,
    excludeIndex = null,
  ) => {
    if (!agentId) return false;

    const idStr = String(agentId).trim();
    if (!idStr) return false;

    if (agentClassification === "AGENT DOUBLE ENDER") {
      // For AGENT DOUBLE ENDER, check if this agent with this transactionType already exists
      return agents.some(
        (a, idx) =>
          (excludeIndex == null || idx !== excludeIndex) &&
          String(a.agentId || "").trim() === idStr &&
          a.classification === "AGENT DOUBLE ENDER" &&
          a.transactionType === transactionType,
      );
    } else {
      // For other classifications, check if agentId already exists
      return agents.some(
        (a, idx) =>
          (excludeIndex == null || idx !== excludeIndex) &&
          String(a.agentId || "").trim() === idStr,
      );
    }
  };

  const startEditAgent = (idx) => {
    const agentToEdit = agents[idx];
    if (!agentToEdit) return;
    setAgent(
      calculateFees({
        ...agentToEdit,
        officeExpenses: normalizeOfficeExpenses(agentToEdit.officeExpenses),
      }),
    );
    setEditingIndex(idx);
    if (agentToEdit.buyerRebateIncluded) {
      setBuyerRebateIncluded(agentToEdit.buyerRebateIncluded);
      setBuyerRebateAmount(agentToEdit.buyerRebateAmount || "");
    } else {
      setBuyerRebateIncluded("no");
      setBuyerRebateAmount("");
    }
    setValidationErrors({});
  };

  const startDeleteAgent = (idx) => {
    setDeletingIndex(idx);
    setShowDeleteModal(true);
  };

  const calculateFees = (agentObj) => {
    const taxOverridden = !!(
      agentObj.taxOverridden === true || agentObj.taxOverridden === "true"
    );

    // Amount from Award × %, unless ADE user typed Amount (then keep entered amount)
    const awardAmount = parseFloat(agentObj.awardAmount) || 0;
    const percentage = parseFloat(agentObj.percentage) || 0;
    const isAde =
      String(agentObj.classification || "").toUpperCase() ===
        "AGENT DOUBLE ENDER" ||
      classification === "AGENT DOUBLE ENDER";
    const preferEnteredAmount =
      isAde && agentObj.preferEnteredAmount === true;
    const enteredAmount = roundTo2(
      parseFloat(String(agentObj.amount ?? "").replace(/,/g, "")) || 0,
    );
    const calculatedAmount =
      preferEnteredAmount && Number.isFinite(enteredAmount)
        ? enteredAmount
        : roundTo2((awardAmount * percentage) / 100);

    // Standard HST on commission (Amount × rate); when override is on, this amount is also included in Total Fees (office share)
    const defaultCommissionTax = roundTo2(calculatedAmount * taxRate);

    const rawTaxInput = String(agentObj.tax ?? "")
      .replace(/,/g, "")
      .trim();
    const parsedOverrideTax =
      rawTaxInput === ""
        ? defaultCommissionTax
        : roundTo2(parseFloat(rawTaxInput) || 0);
    const tax = taxOverridden ? parsedOverrideTax : defaultCommissionTax;

    const total = roundTo2(calculatedAmount + tax);

    // Calculate fees deducted based on fee plan
    let feesDeducted = 0;
    switch (agentObj.feeInfo) {
      case "referralBroker":
        feesDeducted = 0;
        break;
      case "plan250":
        feesDeducted = 250;
        break;
      case "plan500":
        feesDeducted = 500;
        break;
      case "plan9010":
        feesDeducted = roundTo2(calculatedAmount * 0.1); // 10% of amount
        break;
      case "plan8515":
        feesDeducted = roundTo2(calculatedAmount * 0.15); // 15% of amount
        break;
      case "plan955":
        feesDeducted = roundTo2(calculatedAmount * 0.05); // 5% of amount
        break;
      case "plan5050":
        feesDeducted = roundTo2(calculatedAmount * 0.5); // 50% of amount
        break;
      case "plan8020":
        feesDeducted = roundTo2(calculatedAmount * 0.2); // 20% of amount
        break;
      case "plan150":
        feesDeducted = 150;
        break;
      case "flexible":
        feesDeducted = roundTo2(parseFloat(agentObj.feesDeducted) || 0); // Use user input
        break;
      default:
        feesDeducted = 0;
    }

    // Calculate tax on fees using tax rate
    const taxOnFees = roundTo2(feesDeducted * taxRate);
    const baseOfficeFees = roundTo2(feesDeducted + taxOnFees);
    const officeShareHstOnCommission = taxOverridden ? defaultCommissionTax : 0;
    const officeExp = computeOfficeExpenses(agentObj, taxRate);
    const totalFees = roundTo2(
      baseOfficeFees + officeShareHstOnCommission + officeExp.totalWithTax,
    );

    // Calculate net commission
    const netCommission = roundTo2(total - totalFees);

    return {
      ...agentObj,
      taxOverridden,
      preferEnteredAmount: !!preferEnteredAmount,
      officeExpenses: normalizeOfficeExpenses(agentObj.officeExpenses),
      // Keep raw typed amount while editing; otherwise store rounded Amount
      amount: preferEnteredAmount
        ? String(agentObj.amount ?? "")
        : roundTo2Str(calculatedAmount),
      feesDeducted:
        agentObj.feeInfo === "flexible"
          ? agentObj.feesDeducted
          : roundTo2Str(feesDeducted),
      tax: roundTo2Str(tax),
      total: roundTo2Str(total),
      taxOnFees: roundTo2Str(taxOnFees),
      totalFees: roundTo2Str(totalFees),
      officeExpensesTotal: roundTo2Str(officeExp.totalWithTax),
      netCommission: roundTo2Str(netCommission),
    };
  };

  const handleOfficeExpensesEnabledChange = async (enabled) => {
    if (!enabled) {
      setAgent((prev) =>
        calculateFees({
          ...prev,
          officeExpensesEnabled: false,
          officeExpenses: normalizeOfficeExpenses(prev.officeExpenses),
        }),
      );
      return;
    }

    const key =
      agent?.agentId != null && String(agent.agentId).trim() !== ""
        ? String(agent.agentId)
        : "";
    if (!key) {
      setAgent((prev) =>
        calculateFees({
          ...prev,
          officeExpensesEnabled: true,
          officeExpenses: normalizeOfficeExpenses(prev.officeExpenses),
        }),
      );
      return;
    }

    try {
      const res = await axiosInstance.get(
        `/agent-expenses/balances/${encodeURIComponent(key)}`,
      );
      const suggested = res.data?.suggestedAmounts || {};
      const expenses = emptyOfficeExpenses();
      let any = false;
      for (const opt of OFFICE_EXPENSE_OPTIONS) {
        const raw = suggested[opt.key];
        const amt = parseFloat(String(raw ?? "").replace(/,/g, "")) || 0;
        if (amt > 0) {
          expenses[opt.key] = { selected: true, amount: String(amt) };
          any = true;
        }
      }
      setAgent((prev) =>
        calculateFees({
          ...prev,
          officeExpensesEnabled: true,
          officeExpenses: any
            ? expenses
            : normalizeOfficeExpenses(prev.officeExpenses),
        }),
      );
    } catch (err) {
      console.warn("Could not load agent expense balances:", err);
      setAgent((prev) =>
        calculateFees({
          ...prev,
          officeExpensesEnabled: true,
          officeExpenses: normalizeOfficeExpenses(prev.officeExpenses),
        }),
      );
    }
  };

  const handleOfficeExpenseTaxRateChange = (percentValue) => {
    const pct = String(percentValue ?? "").trim();
    const stored = pct === "" ? "" : String(parseFloat(pct) / 100);
    setAgent((prev) =>
      calculateFees({
        ...prev,
        officeExpensesTaxRate: stored,
        officeExpenses: normalizeOfficeExpenses(prev.officeExpenses),
      }),
    );
  };

  const handleOfficeExpenseItemChange = (key, field, value) => {
    setAgent((prev) => {
      const expenses = normalizeOfficeExpenses(prev.officeExpenses);
      expenses[key] = {
        ...expenses[key],
        [field]: value,
      };
      return calculateFees({
        ...prev,
        officeExpenses: expenses,
      });
    });
  };

  const handleAddAgent = (e) => {
    e.preventDefault();

    // Validate mandatory fields
    if (!validateForm()) {
      return;
    }

    // When Referral is checked, add to Agent Info table with classification "Referral"
    if (isReferral) {
      const agentWithRef = {
        ...agent,
        classification: "Referral",
        transactionType: agent.transactionType || "",
      };
      setPendingAgent(agentWithRef);
      setPendingAgentsToAdd([]);
      setShowBuyerRebateModal(true);
      setIsReferral(false);
      return;
    }

    // For AGENT DOUBLE ENDER: add LISTING and SELLING separately (set Percentage on each)
    if (classification === "AGENT DOUBLE ENDER") {
      const hasListing = agents.some(
        (a) =>
          a.agentId === agent.agentId &&
          a.classification === "AGENT DOUBLE ENDER" &&
          a.transactionType === "LISTING",
      );
      const hasSelling = agents.some(
        (a) =>
          a.agentId === agent.agentId &&
          a.classification === "AGENT DOUBLE ENDER" &&
          a.transactionType === "SELLING",
      );

      if (hasListing && hasSelling) {
        toast.error("This agent already has both listing and selling entries.");
        return;
      }

      const transactionType =
        agent.transactionType ||
        (!hasListing ? "LISTING" : "SELLING");

      if (
        (transactionType === "LISTING" && hasListing) ||
        (transactionType === "SELLING" && hasSelling)
      ) {
        toast.error(
          `This agent already has a ${transactionType.toLowerCase()} entry.`,
        );
        return;
      }

      const normalized = normalizeAdeAwardAndPercent(
        { ...agent, transactionType },
        commissionData,
      );
      const agentWithTransactionType = calculateFees(normalized);
      if (!validateCommissionCaps(agentWithTransactionType)) {
        return;
      }
      setPendingAgentsToAdd([]);
      setPendingAgent(agentWithTransactionType);
      setShowBuyerRebateModal(true);
      return;
    }

    // Non-AGENT DOUBLE ENDER: single agent add
    const alreadyAdded = isAgentAlreadyAdded(
      agent.agentId,
      agent.classification,
      agent.transactionType,
    );
    if (alreadyAdded) {
      toast.error("This agent has already been added to the list.");
      return;
    }
    if (!validateCommissionCaps(agent)) {
      return;
    }
    setPendingAgentsToAdd([]);
    setPendingAgent(agent);
    setShowBuyerRebateModal(true);
  };

  const handleCancelBuyerRebate = () => {
    setShowBuyerRebateModal(false);
    setBuyerRebateIncluded("no");
    setBuyerRebateAmount("");
    setPendingAgent(null);
    setPendingAgentsToAdd([]);
  };

  const handleEdit = () => {
    if (agents.length === 0) return;
    startEditAgent(0);
  };

  const handleUpdateAgent = (e) => {
    e.preventDefault();

    // Validate mandatory fields
    if (!validateForm()) {
      return;
    }

    let agentToUpdate = agent;
    if (classification === "AGENT DOUBLE ENDER") {
      agentToUpdate = calculateFees(
        normalizeAdeAwardAndPercent(agent, commissionData),
      );
    }
    if (!validateCommissionCaps(agentToUpdate)) {
      return;
    }

    // Store the agent data and show buyer rebate popup for update
    setPendingAgent(agentToUpdate);
    setShowBuyerRebateModal(true);
  };

  const handleBuyerRebateSubmit = () => {
    if (!pendingAgent && pendingAgentsToAdd.length === 0) return;

    // When adding both LISTING and SELLING for AGENT DOUBLE ENDER (pendingAgentsToAdd has 2)
    if (pendingAgentsToAdd.length > 0) {
      let agentsToAdd = [...pendingAgentsToAdd];
      // Apply buyer rebate only to the SELLING agent
      const sellingIdx = agentsToAdd.findIndex(
        (a) => a.transactionType === "SELLING",
      );
      if (
        sellingIdx >= 0 &&
        buyerRebateIncluded === "yes" &&
        buyerRebateAmount
      ) {
        const sellingAgent = agentsToAdd[sellingIdx];
        const rebateAmount = parseFloat(buyerRebateAmount) || 0;
        const grossAmount = parseFloat(sellingAgent.amount) || 0;
        const sellingTax = parseFloat(sellingAgent.tax) || 0;
        const finalTotal = roundTo2(grossAmount + sellingTax);
        const totalFees = parseFloat(sellingAgent.totalFees) || 0;
        const finalNetCommission = roundTo2(
          finalTotal - totalFees - rebateAmount,
        );
        agentsToAdd[sellingIdx] = {
          ...sellingAgent,
          amount: roundTo2Str(grossAmount),
          total: roundTo2Str(finalTotal),
          netCommission: roundTo2Str(finalNetCommission),
          buyerRebateIncluded: "yes",
          buyerRebateAmount: buyerRebateAmount,
        };
      } else if (sellingIdx >= 0 && buyerRebateIncluded === "yes") {
        agentsToAdd[sellingIdx] = {
          ...agentsToAdd[sellingIdx],
          buyerRebateIncluded: "yes",
          buyerRebateAmount: buyerRebateAmount || "",
        };
      }

      const newAgents = [...agents, ...agentsToAdd];
      setAgents(newAgents);
      setPendingAgentsToAdd([]);
      setPendingAgent(null);

      // Reset form for next agent (keep classification)
      setAgent({
        ...initialAgent,
        classification: agent.classification,
        transactionType: "LISTING",
        agentId: "",
        agentName: "",
      });
      setShowBuyerRebateModal(false);
      setBuyerRebateIncluded("no");
      setBuyerRebateAmount("");
      setSelectedListingAgentIndex(0);
      return;
    }

    if (!pendingAgent) return;

    // Buyer rebate: keep full commission in Amount/Tax/Total; deduct rebate from Net only
    let finalAmount = parseFloat(pendingAgent.amount) || 0;
    let finalTax = parseFloat(pendingAgent.tax) || 0;
    let finalTotal = parseFloat(pendingAgent.total) || 0;
    let finalNetCommission = parseFloat(pendingAgent.netCommission) || 0;

    if (buyerRebateIncluded === "yes" && buyerRebateAmount) {
      const rebateAmount = parseFloat(buyerRebateAmount) || 0;
      finalAmount = parseFloat(pendingAgent.amount) || 0;
      finalTax = parseFloat(pendingAgent.tax) || 0;
      finalTotal = roundTo2(finalAmount + finalTax);
      const totalFees = parseFloat(pendingAgent.totalFees) || 0;
      finalNetCommission = roundTo2(finalTotal - totalFees - rebateAmount);
    }

    const agentWithRebate = {
      ...pendingAgent,
      amount: finalAmount.toFixed(2),
      total: finalTotal.toFixed(2),
      netCommission: finalNetCommission.toFixed(2),
      buyerRebateIncluded: buyerRebateIncluded,
      buyerRebateAmount: buyerRebateIncluded === "yes" ? buyerRebateAmount : "",
    };

    let agentsAfterSubmit;
    if (editingIndex !== null) {
      const updatedAgents = [...agents];
      updatedAgents[editingIndex] = agentWithRebate;
      setAgents(updatedAgents);
      setEditingIndex(null);
      agentsAfterSubmit = updatedAgents;
    } else {
      agentsAfterSubmit = [...agents, agentWithRebate];
      setAgents(agentsAfterSubmit);
    }

    const useSplitPercent =
      classification === "LISTING SIDE" ||
      classification === "CO-OPERATING SIDE" ||
      classification === "AGENT DOUBLE ENDER";

    const nextPercentageForForm = useSplitPercent
      ? remainingPercentAfterAgents(agentsAfterSubmit)
      : initialAgent.percentage;

    // Determine next transaction type for AGENT DOUBLE ENDER
    let nextTransactionType = "";
    if (classification === "AGENT DOUBLE ENDER") {
      const updatedAgentsList = agentsAfterSubmit;

      const hasListing = updatedAgentsList.some(
        (a) =>
          a.classification === "AGENT DOUBLE ENDER" &&
          a.transactionType === "LISTING" &&
          a.agentId === agentWithRebate.agentId,
      );
      const hasSelling = updatedAgentsList.some(
        (a) =>
          a.classification === "AGENT DOUBLE ENDER" &&
          a.transactionType === "SELLING" &&
          a.agentId === agentWithRebate.agentId,
      );

      if (agentWithRebate.transactionType === "LISTING" && !hasSelling) {
        nextTransactionType = "SELLING";
      } else if (agentWithRebate.transactionType === "SELLING" && !hasListing) {
        nextTransactionType = "LISTING";
      } else {
        // Both transactions added, allow user to select or start fresh
        nextTransactionType = "LISTING";
      }
    }

    setAgent({
      ...initialAgent,
      classification: agent.classification, // Preserve classification
      transactionType: nextTransactionType, // Set next transaction type for AGENT DOUBLE ENDER
      percentage: nextPercentageForForm,
      agentId:
        classification === "AGENT DOUBLE ENDER" ? agentWithRebate.agentId : "", // Keep agent ID for AGENT DOUBLE ENDER to allow second transaction
      agentName:
        classification === "AGENT DOUBLE ENDER"
          ? agentWithRebate.agentName
          : "", // Keep agent name for AGENT DOUBLE ENDER
    });
    setSelectedListingAgentIndex(0); // Reset to first available agent

    // Recalculate award amount for the next agent
    if (classification && commissionData) {
      let calculatedAwardAmount = "0.00";
      if (classification === "LISTING SIDE") {
        const listingAmount = parseFloat(commissionData.listingAmount) || 0;
        calculatedAwardAmount = listingAmount.toFixed(2);
      } else if (classification === "SELLING SIDE") {
        const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
        calculatedAwardAmount = sellingAmount.toFixed(2);
      } else if (classification === "CO-OPERATING SIDE") {
        const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
        calculatedAwardAmount = sellingAmount.toFixed(2);
      } else if (classification === "AGENT DOUBLE ENDER") {
        // Full pool as award; next % = 100 − sum of all sides already posted
        const normalized = normalizeAdeAwardAndPercent(
          {
            ...agent,
            transactionType: nextTransactionType,
            percentage: String(nextPercentageForForm),
          },
          commissionData,
        );
        calculatedAwardAmount = normalized.awardAmount;
        setAgent((prev) =>
          calculateFees({
            ...prev,
            ...normalized,
            transactionType: nextTransactionType,
            awardAmount: calculatedAwardAmount,
            percentage: String(nextPercentageForForm),
          }),
        );
      }

      if (classification !== "AGENT DOUBLE ENDER") {
        setAgent((prev) => ({
          ...prev,
          awardAmount: calculatedAwardAmount,
          transactionType: nextTransactionType,
        }));
      }
    }

    // Reset popup state
    setShowBuyerRebateModal(false);
    setBuyerRebateIncluded("no");
    setBuyerRebateAmount("");
    setPendingAgent(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setAgent({
      ...initialAgent,
      classification: agent.classification, // Preserve classification
    });

    // Reset selected listing agent index to show available agents
    setSelectedListingAgentIndex(0);

    // Recalculate award amount after canceling edit
    if (classification && commissionData) {
      let calculatedAwardAmount = "0.00";
      if (classification === "LISTING SIDE") {
        const listingAmount = parseFloat(commissionData.listingAmount) || 0;
        calculatedAwardAmount = listingAmount.toFixed(2);
      } else if (classification === "SELLING SIDE") {
        const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
        calculatedAwardAmount = sellingAmount.toFixed(2);
      } else if (classification === "CO-OPERATING SIDE") {
        const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
        calculatedAwardAmount = sellingAmount.toFixed(2);
      }

      setAgent((prev) => ({
        ...prev,
        awardAmount: calculatedAwardAmount,
      }));
    }

    // Auto-populate agent information from selected listing if available
    if (
      selectedListing &&
      (classification === "LISTING SIDE" ||
        classification === "AGENT DOUBLE ENDER")
    ) {
      const availableAgents = getAvailableListingAgents();
      if (availableAgents.length > 0) {
        const firstAvailableAgent = availableAgents[0];
        const agentId = firstAvailableAgent.agentNo?.toString() || "";
        const agentName = `${firstAvailableAgent.firstName || ""} ${
          firstAvailableAgent.lastName || ""
        }`.trim();

        if (agentId && agentName) {
          setAgent((prev) => ({
            ...prev,
            agentId: agentId,
            agentName: agentName,
          }));
        }
      }
    }

    setBuyerRebateIncluded("no");
    setBuyerRebateAmount("");
    setValidationErrors({});
  };

  const handleDelete = () => {
    if (agents.length === 0) return;
    startDeleteAgent(0);
  };

  const handleConfirmDelete = () => {
    if (deletingIndex !== null) {
      const updatedAgents = agents.filter(
        (_, index) => index !== deletingIndex,
      );
      setAgents(updatedAgents);

      // Reset the form and recalculate award amount after deletion
      setAgent({
        ...initialAgent,
        classification: agent.classification, // Preserve classification
        alreadyAdded: false, // Reset the alreadyAdded flag
      });

      // Reset selected listing agent index to show available agents
      setSelectedListingAgentIndex(0);

      // Trigger award amount recalculation
      if (classification && commissionData) {
        let calculatedAwardAmount = "0.00";
        if (classification === "LISTING SIDE") {
          const listingAmount = parseFloat(commissionData.listingAmount) || 0;
          calculatedAwardAmount = listingAmount.toFixed(2);
        } else if (classification === "SELLING SIDE") {
          const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
          calculatedAwardAmount = sellingAmount.toFixed(2);
        } else if (classification === "CO-OPERATING SIDE") {
          const sellingAmount = parseFloat(commissionData.sellingAmount) || 0;
          calculatedAwardAmount = sellingAmount.toFixed(2);
        }

        setAgent((prev) => ({
          ...prev,
          awardAmount: calculatedAwardAmount,
        }));
      }

      // Auto-populate agent information from selected listing if available
      if (
        selectedListing &&
        (classification === "LISTING SIDE" ||
          classification === "AGENT DOUBLE ENDER")
      ) {
        const availableAgents = getAvailableListingAgents();
        if (availableAgents.length > 0) {
          const firstAvailableAgent = availableAgents[0];
          const agentId = firstAvailableAgent.agentNo?.toString() || "";
          const agentName = `${firstAvailableAgent.firstName || ""} ${
            firstAvailableAgent.lastName || ""
          }`.trim();

          if (agentId && agentName) {
            // Calculate award amount for auto-population
            let calculatedAwardAmount = "0.00";
            if (classification === "LISTING SIDE") {
              const listingAmount =
                parseFloat(commissionData.listingAmount) || 0;
              calculatedAwardAmount = listingAmount.toFixed(2);
            } else if (classification === "SELLING SIDE") {
              const sellingAmount =
                parseFloat(commissionData.sellingAmount) || 0;
              calculatedAwardAmount = sellingAmount.toFixed(2);
            } else if (classification === "CO-OPERATING SIDE") {
              const sellingAmount =
                parseFloat(commissionData.sellingAmount) || 0;
              calculatedAwardAmount = sellingAmount.toFixed(2);
            } else if (classification === "AGENT DOUBLE ENDER") {
              const normalized = normalizeAdeAwardAndPercent(
                {
                  percentage: String(defaultAdeListingPercent(commissionData)),
                  transactionType: "LISTING",
                },
                commissionData,
              );
              setAgent((prev) =>
                calculateFees({
                  ...prev,
                  agentId: agentId,
                  agentName: agentName,
                  ...normalized,
                  transactionType: "LISTING",
                }),
              );
            } else {
              setAgent((prev) => ({
                ...prev,
                agentId: agentId,
                agentName: agentName,
                awardAmount: calculatedAwardAmount,
              }));
            }
          }
        }
      }
    }
    setShowDeleteModal(false);
    setDeletingIndex(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingIndex(null);
  };

  // Sale Closing Info Handlers (for table only)
  const handleSaleClosingChange = (e) => {
    const { name, value } = e.target;
    setSaleClosing((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddSaleClosing = (e) => {
    e.preventDefault();
    setSaleClosingRows([...saleClosingRows, saleClosing]);
    setSaleClosing(initialSaleClosing);
  };

  const handleSaveTradeClick = async () => {
    // Prevent multiple clicks if already saving or trade is already saved
    if (isSaving || isTradeSaved) {
      return;
    }

    console.log(
      "Attempting to save trade. Current list of added agents:",
      agents,
    );

    // Check if the form has data for a new agent that hasn't been added yet.
    const isDirty =
      agent.agentName !== "" &&
      agent.agentId !== "" &&
      editingIndex === null &&
      !agent.alreadyAdded;

    if (isDirty) {
      console.log("Unsaved agent form data detected:", agent);
      alert(
        "You have unsaved agent information in the form. Please click 'Add Agent' to add it to the list before saving the entire trade.",
      );
      return;
    }

    try {
      setIsSaving(true);
      const saved = await onSaveTrade();
      if (saved) {
        setIsTradeSaved(true);
      }
    } catch (error) {
      console.error("Error saving trade:", error);
      toast.error("Failed to save trade. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const isListingOrAde =
    agent.classification === "LISTING SIDE" ||
    agent.classification === "AGENT DOUBLE ENDER";
  const availableListingAgents = getAvailableListingAgents();
  const availableListingOfficeAgents = getAvailableListingSideOfficeAgents();
  const showListingAgentsDropdown =
    isListingOrAde &&
    listingAgentsSource.length > 0 &&
    availableListingAgents.length > 0;
  const showListingAgentsExhausted =
    isListingOrAde &&
    listingAgentsSource.length > 0 &&
    availableListingAgents.length === 0;
  const showListingOfficeDropdown =
    agent.classification === "LISTING SIDE" &&
    listingAgentsSource.length === 0 &&
    availableListingOfficeAgents.length > 0;
  const showListingOfficeExhausted =
    agent.classification === "LISTING SIDE" &&
    listingAgentsSource.length === 0 &&
    availableListingOfficeAgents.length === 0;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-2">Agent Commission Information</h2>
      <p className="text-sm text-gray-600 mb-4">
        Applied tax rate: <strong>{Math.round(taxRate * 100)}%</strong>
        {taxRate !== DEFAULT_TAX_RATE && (
          <span className="ml-2 text-blue-600">(custom)</span>
        )}
      </p>
      <form
        className="mb-6"
        onSubmit={editingIndex !== null ? handleUpdateAgent : handleAddAgent}
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agent <span className="text-red-500">*</span>
            </label>
            {agent.classification === "CO-OPERATING SIDE" &&
            getAvailableCoOperatingAgents().length > 0 ? (
              // Dropdown for CO-OPERATING SIDE with available agents
              <select
                name="agentSelect"
                value={
                  agentList.find((a) => a.employeeNo === agent.agentId)?._id ||
                  ""
                }
                onChange={handleAgentSelect}
                className={`block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 ${
                  validationErrors.agentId
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select an agent</option>
                {getAvailableCoOperatingAgents().map((agentItem) => (
                  <option key={agentItem._id} value={agentItem._id}>
                    {agentItem.employeeNo} - {agentItem.firstName}{" "}
                    {agentItem.lastName}
                  </option>
                ))}
              </select>
            ) : agent.classification === "CO-OPERATING SIDE" &&
              getAvailableCoOperatingAgents().length === 0 ? (
              // Empty text inputs when all CO-OPERATING SIDE agents have been added
              <>
                <input
                  name="agentId"
                  value=""
                  onChange={handleAgentInputChange}
                  placeholder="ID"
                  className="block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 border-gray-300"
                  readOnly
                />
                <input
                  name="agentName"
                  value=""
                  onChange={handleAgentInputChange}
                  placeholder="Agent Name"
                  className="block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 border-gray-300"
                  readOnly
                />
              </>
            ) : agent.classification === "CO-OPERATING SIDE" ? (
              // Fallback dropdown for CO-OPERATING SIDE (should not reach here normally)
              <select
                name="agentSelect"
                value={
                  agentList.find((a) => a.employeeNo === agent.agentId)?._id ||
                  ""
                }
                onChange={handleAgentSelect}
                className={`block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 ${
                  validationErrors.agentId
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select an agent</option>
                {agentList.map((agentItem) => (
                  <option key={agentItem._id} value={agentItem._id}>
                    {agentItem.employeeNo} - {agentItem.firstName}{" "}
                    {agentItem.lastName}
                  </option>
                ))}
              </select>
            ) : showListingAgentsDropdown ? (
              <select
                name="listingAgentSelect"
                value={selectedListingAgentIndex}
                onChange={handleListingAgentSelect}
                className={`block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 ${
                  validationErrors.agentId
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select an agent</option>
                {availableListingAgents.map((agentItem, index) => (
                  <option key={`${agentItem.agentNo || "agent"}-${index}`} value={index}>
                    {agentItem.agentNo} - {agentItem.firstName}{" "}
                    {agentItem.lastName}
                  </option>
                ))}
              </select>
            ) : showListingAgentsExhausted ? (
              <>
                <input
                  name="agentId"
                  value=""
                  onChange={handleAgentInputChange}
                  placeholder="ID"
                  className="block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 border-gray-300"
                  readOnly
                />
                <input
                  name="agentName"
                  value=""
                  onChange={handleAgentInputChange}
                  placeholder="Agent Name"
                  className="block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 border-gray-300"
                  readOnly
                />
              </>
            ) : showListingOfficeDropdown ? (
              <select
                name="agentSelect"
                value={
                  agentList.find(
                    (a) => String(a.employeeNo) === String(agent.agentId),
                  )?._id || ""
                }
                onChange={handleAgentSelect}
                className={`block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 ${
                  validationErrors.agentId
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select an agent</option>
                {availableListingOfficeAgents.map((agentItem) => (
                  <option key={agentItem._id} value={agentItem._id}>
                    {agentItem.employeeNo} - {agentItem.firstName}{" "}
                    {agentItem.lastName}
                  </option>
                ))}
              </select>
            ) : showListingOfficeExhausted ? (
              <>
                <input
                  name="agentId"
                  value=""
                  onChange={handleAgentInputChange}
                  placeholder="ID"
                  className="block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 border-gray-300"
                  readOnly
                />
                <input
                  name="agentName"
                  value=""
                  onChange={handleAgentInputChange}
                  placeholder="Agent Name"
                  className="block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 border-gray-300"
                  readOnly
                />
              </>
            ) : (
              // Text inputs for other classifications or single agent listings
              <>
                <input
                  name="agentId"
                  value={agent.agentId}
                  onChange={handleAgentInputChange}
                  placeholder="ID"
                  className={`block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 mb-1 ${
                    validationErrors.agentId
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  readOnly={
                    agent.classification === "LISTING SIDE" ||
                    agent.classification === "AGENT DOUBLE ENDER"
                  }
                />
                <input
                  name="agentName"
                  value={agent.agentName}
                  onChange={handleAgentInputChange}
                  placeholder="Agent Name"
                  className={`block w-full rounded-md shadow-sm focus:border-blue-700 focus:ring-blue-700 ${
                    validationErrors.agentName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  readOnly={
                    agent.classification === "LISTING SIDE" ||
                    agent.classification === "AGENT DOUBLE ENDER"
                  }
                />
              </>
            )}
            {showListingAgentsDropdown && (
                <p className="text-xs text-gray-500 mt-1">
                  Select from remaining listing agents
                </p>
              )}
            {showListingAgentsExhausted && (
                <p className="text-xs text-gray-500 mt-1">
                  All listing agents have been added
                </p>
              )}
            {showListingOfficeDropdown && (
                <p className="text-xs text-gray-500 mt-1">
                  Select an agent
                </p>
              )}
            {showListingOfficeExhausted && (
                <p className="text-xs text-gray-500 mt-1">
                  All listing agents have been added
                </p>
              )}
            {agent.classification === "CO-OPERATING SIDE" &&
              getAvailableCoOperatingAgents().length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Select from remaining co-operating agents
                </p>
              )}
            {agent.classification === "CO-OPERATING SIDE" &&
              getAvailableCoOperatingAgents().length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  All co-operating agents have been added
                </p>
              )}
            {agent.classification === "AGENT DOUBLE ENDER" &&
              agent.agentId &&
              (() => {
                const idStr = String(agent.agentId || "").trim();
                const hasListing = agents.some(
                  (a) =>
                    String(a.agentId || "").trim() === idStr &&
                    a.classification === "AGENT DOUBLE ENDER" &&
                    a.transactionType === "LISTING",
                );
                const hasSelling = agents.some(
                  (a) =>
                    String(a.agentId || "").trim() === idStr &&
                    a.classification === "AGENT DOUBLE ENDER" &&
                    a.transactionType === "SELLING",
                );
                if (agent.transactionType === "LISTING" && hasListing) {
                  return (
                    <p className="text-xs text-red-500 mt-1 font-medium">
                      This agent with Listing transaction type has already been
                      added
                    </p>
                  );
                }
                if (agent.transactionType === "SELLING" && hasSelling) {
                  return (
                    <p className="text-xs text-red-500 mt-1 font-medium">
                      This agent with Selling transaction type has already been
                      added
                    </p>
                  );
                }
                if (hasListing && hasSelling) {
                  return (
                    <p className="text-xs text-orange-500 mt-1 font-medium">
                      Both transactions for this agent have been added
                    </p>
                  );
                }
                if (
                  hasListing &&
                  !hasSelling &&
                  agent.transactionType !== "SELLING"
                ) {
                  return (
                    <p className="text-xs text-blue-500 mt-1">
                      Listing transaction added. You can add Selling transaction
                      for this agent.
                    </p>
                  );
                }
                if (
                  hasSelling &&
                  !hasListing &&
                  agent.transactionType !== "LISTING"
                ) {
                  return (
                    <p className="text-xs text-blue-500 mt-1">
                      Selling transaction added. You can add Listing transaction
                      for this agent.
                    </p>
                  );
                }
                return null;
              })()}
            {agent.classification !== "AGENT DOUBLE ENDER" &&
              agent.alreadyAdded && (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  This agent has already been added to the list
                </p>
              )}
            {validationErrors.agentId && (
              <p className="text-red-500 text-xs mt-1">
                {validationErrors.agentId}
              </p>
            )}
            {validationErrors.agentName && (
              <p className="text-red-500 text-xs mt-1">
                {validationErrors.agentName}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Classification
            </label>
            <div className="flex items-center gap-2">
              <input
                name="classification"
                value={isReferral ? "Referral" : agent.classification}
                readOnly
                className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-700 focus:ring-blue-700"
              />
              <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isReferral}
                  disabled={agent.classification === "Referral"}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      feeInfoBeforeReferralRef.current =
                        agent.feeInfo && agent.feeInfo !== "referralBroker"
                          ? agent.feeInfo
                          : feeInfoBeforeReferralRef.current;
                      setIsReferral(true);
                      setAgent((prev) =>
                        calculateFees({
                          ...prev,
                          feeInfo: "referralBroker",
                          feesDeducted: "0.00",
                        }),
                      );
                    } else {
                      setIsReferral(false);
                      const restore =
                        feeInfoBeforeReferralRef.current &&
                        feeInfoBeforeReferralRef.current !== "referralBroker"
                          ? feeInfoBeforeReferralRef.current
                          : "plan500";
                      feeInfoBeforeReferralRef.current = null;
                      setAgent((prev) =>
                        calculateFees({ ...prev, feeInfo: restore }),
                      );
                    }
                  }}
                  className="rounded border-gray-300"
                />
                Referral
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {isReferral
                ? "Referral shows in Agent Info table; on Trade Details Sheet appears under Outside Brokers (GL 51100)"
                : "Automatically populated from Key Info Form"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lead
            </label>
            <select
              name="lead"
              value={agent.lead}
              onChange={handleAgentInputChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              YTD Commission
            </label>
            <select
              name="ytdCommission"
              value={agent.ytdCommission}
              onChange={handleAgentInputChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fee Info
            </label>
            <select
              name="feeInfo"
              value={isReferral ? "referralBroker" : agent.feeInfo}
              onChange={handleAgentInputChange}
              disabled={isReferral}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {feePlans.map((plan) => (
                <option key={plan.value} value={plan.value}>
                  {plan.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Award Amount
            </label>
            <input
              name="awardAmount"
              value={agent.awardAmount}
              onChange={handleAgentInputChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 bg-gray-100"
              readOnly
            />
            <p className="text-xs text-gray-500 mt-1">
              {agent.classification === "AGENT DOUBLE ENDER"
                ? "Total commission pool (listing + selling). Set Percentage for this side."
                : "Auto-calculated from Commission Income selling amount"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Percentage
            </label>
            <input
              name="percentage"
              value={agent.percentage}
              onChange={handleAgentInputChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
            />
            {agent.classification === "AGENT DOUBLE ENDER" && (
              <p className="text-xs text-gray-500 mt-1">
                Share of the total pool for this side. After each add, the next
                percentage is filled as 100 − sum already posted. Listing
                amounts cannot exceed Commission Income listing ($
                {roundTo2Str(
                  parseFloat(commissionData?.listingAmount) || 0,
                )}
                ); selling amounts cannot exceed selling ($
                {roundTo2Str(
                  parseFloat(commissionData?.sellingAmount) || 0,
                )}
                ).
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <input
              name="amount"
              value={agent.amount}
              onChange={handleAgentInputChange}
              onBlur={() => {
                if (
                  classification !== "AGENT DOUBLE ENDER" &&
                  agent.classification !== "AGENT DOUBLE ENDER"
                ) {
                  return;
                }
                const award =
                  parseFloat(
                    String(agent.awardAmount || "").replace(/,/g, ""),
                  ) || 0;
                const amt = roundTo2(
                  parseFloat(String(agent.amount || "").replace(/,/g, "")) ||
                    0,
                );
                const pct = award > 0 ? roundTo2((amt / award) * 100) : 0;
                setAgent(
                  calculateFees({
                    ...agent,
                    amount: roundTo2Str(amt),
                    percentage: pct.toFixed(2),
                    preferEnteredAmount: true,
                  }),
                );
              }}
              readOnly={
                classification !== "AGENT DOUBLE ENDER" &&
                agent.classification !== "AGENT DOUBLE ENDER"
              }
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 ${
                classification !== "AGENT DOUBLE ENDER" &&
                agent.classification !== "AGENT DOUBLE ENDER"
                  ? "bg-gray-100"
                  : ""
              }`}
            />
            {(classification === "AGENT DOUBLE ENDER" ||
              agent.classification === "AGENT DOUBLE ENDER") && (
              <p className="text-xs text-gray-500 mt-1">
                Enter amount to auto-fill Percentage (2 decimals), or set
                Percentage to calculate Amount.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax
            </label>
            <label className="flex items-center gap-2 mb-1 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={!!agent.taxOverridden}
                onChange={(e) => {
                  const on = e.target.checked;
                  setAgent((prev) =>
                    calculateFees({ ...prev, taxOverridden: on }),
                  );
                }}
                className="rounded border-gray-300"
              />
              Override commission HST
            </label>
            <input
              name="tax"
              value={agent.tax}
              onChange={handleAgentInputChange}
              readOnly={!agent.taxOverridden}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 ${
                agent.taxOverridden ? "" : "bg-gray-100"
              }`}
            />
            {agent.taxOverridden ? (
              <p className="text-xs text-gray-500 mt-1">
                Standard HST on commission (Amount × {Math.round(taxRate * 100)}
                %) is included in <strong>Total Fees</strong> (office share).
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                HST on commission (Amount × {Math.round(taxRate * 100)}%).
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total
            </label>
            <input
              name="total"
              value={agent.total}
              readOnly
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fees Deducted
            </label>
            <input
              name="feesDeducted"
              value={agent.feesDeducted}
              onChange={handleAgentInputChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
              readOnly={agent.feeInfo !== "flexible" || isReferral}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Fees
            </label>
            <input
              name="totalFees"
              value={agent.totalFees}
              readOnly
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Net Commission
            </label>
            <input
              name="netCommission"
              value={agent.netCommission}
              readOnly
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 bg-gray-100"
            />
          </div>
        </div>

        <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <input
              type="checkbox"
              checked={!!agent.officeExpensesEnabled}
              onChange={(e) =>
                handleOfficeExpensesEnabledChange(e.target.checked)
              }
              className="rounded border-gray-300"
            />
            Office Expenses
          </label>
          {agent.officeExpensesEnabled && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs font-medium text-gray-600">
                  Tax rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={officeExpensesTaxRatePercent(agent, taxRate)}
                  onChange={(e) =>
                    handleOfficeExpenseTaxRateChange(e.target.value)
                  }
                  className="w-24 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-700 focus:ring-blue-700"
                />
                <span className="text-xs text-gray-500">
                  Applied to each selected expense (default{" "}
                  {Math.round(taxRate * 100)}%)
                </span>
              </div>
              {OFFICE_EXPENSE_OPTIONS.map((opt) => {
                const row = normalizeOfficeExpenses(agent.officeExpenses)[
                  opt.key
                ];
                const expenseTaxRate = resolveOfficeExpensesTaxRate(
                  agent,
                  taxRate,
                );
                const amt = parseFloat(row.amount) || 0;
                const lineTax =
                  row.selected && amt > 0
                    ? roundTo2(amt * expenseTaxRate)
                    : 0;
                const lineTotal =
                  row.selected && amt > 0 ? roundTo2(amt + lineTax) : 0;
                return (
                  <div
                    key={opt.key}
                    className="flex flex-wrap items-center gap-3 text-sm"
                  >
                    <label className="flex items-center gap-2 min-w-[11rem]">
                      <input
                        type="checkbox"
                        checked={!!row.selected}
                        onChange={(e) =>
                          handleOfficeExpenseItemChange(
                            opt.key,
                            "selected",
                            e.target.checked,
                          )
                        }
                        className="rounded border-gray-300"
                      />
                      {opt.label}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={!row.selected}
                      value={row.amount}
                      onChange={(e) =>
                        handleOfficeExpenseItemChange(
                          opt.key,
                          "amount",
                          e.target.value,
                        )
                      }
                      placeholder="Amount"
                      className="w-32 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-700 focus:ring-blue-700 disabled:bg-gray-100"
                    />
                    {row.selected && amt > 0 && (
                      <span className="text-xs text-gray-600 tabular-nums">
                        + tax ${roundTo2Str(lineTax)} = ${roundTo2Str(lineTotal)}
                      </span>
                    )}
                  </div>
                );
              })}
              <p className="text-sm text-gray-700 pt-1 border-t border-gray-200">
                <span className="font-medium">Office expenses total</span>{" "}
                (incl. tax, added to office share):{" "}
                <span className="font-semibold tabular-nums">
                  ${agent.officeExpensesTotal || "0.00"}
                </span>
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end space-x-3">
          {editingIndex !== null && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className={`px-6 py-2 rounded font-semibold ${
              editingIndex !== null
                ? "bg-blue-900 text-white"
                : classification === "AGENT DOUBLE ENDER" &&
                    isAgentAlreadyAdded(
                      agent.agentId,
                      agent.classification,
                      agent.transactionType,
                    )
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : classification !== "AGENT DOUBLE ENDER" &&
                      agent.alreadyAdded
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-blue-900 text-white"
            }`}
            disabled={
              editingIndex !== null
                ? false
                : classification === "AGENT DOUBLE ENDER"
                  ? isAgentAlreadyAdded(
                      agent.agentId,
                      agent.classification,
                      agent.transactionType,
                    )
                  : agent.alreadyAdded
            }
          >
            {editingIndex !== null
              ? "Update Agent"
              : classification === "AGENT DOUBLE ENDER" &&
                  isAgentAlreadyAdded(
                    agent.agentId,
                    agent.classification,
                    agent.transactionType,
                  )
                ? "Agent Transaction Already Added"
                : classification !== "AGENT DOUBLE ENDER" && agent.alreadyAdded
                  ? "Agent Already Added"
                  : "Add Agent"}
          </button>
        </div>
      </form>

      {/* Buyer Rebate Modal */}
      {showBuyerRebateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Buyer Rebate Confirmation
            </h3>
            <p className="mb-4">Is buyer rebate included in this commission?</p>

            <div className="mb-4">
              <label className="flex items-center mb-2">
                <input
                  type="radio"
                  name="buyerRebateIncluded"
                  value="yes"
                  checked={buyerRebateIncluded === "yes"}
                  onChange={(e) => setBuyerRebateIncluded(e.target.value)}
                  className="mr-2"
                />
                Yes
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buyerRebateIncluded"
                  value="no"
                  checked={buyerRebateIncluded === "no"}
                  onChange={(e) => setBuyerRebateIncluded(e.target.value)}
                  className="mr-2"
                />
                No
              </label>
            </div>

            {buyerRebateIncluded === "yes" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buyer Rebate Amount
                </label>
                <input
                  type="number"
                  value={buyerRebateAmount}
                  onChange={(e) => setBuyerRebateAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
            )}

            {buyerRebateIncluded === "yes" && buyerRebateAmount && (
              <div className="mb-4 p-3 bg-gray-100 rounded">
                <p className="text-sm">
                  <strong>Net before rebate:</strong> $
                  {pendingAgent?.netCommission}
                  <br />
                  <strong>Buyer Rebate (deducted from net only):</strong> $
                  {buyerRebateAmount}
                  <br />
                  <strong>Final Net Commission:</strong> $
                  {roundTo2(
                    parseFloat(pendingAgent?.total || 0) -
                      parseFloat(pendingAgent?.totalFees || 0) -
                      parseFloat(buyerRebateAmount || 0),
                  ).toFixed(2)}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelBuyerRebate}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBuyerRebateSubmit}
                className="px-4 py-2 bg-blue-900 text-white rounded hover:bg-blue-800"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Confirmation</h3>
            <p className="mb-4">
              Are you sure you want to delete this agent commission record?
            </p>
            <p className="mb-6 text-sm text-gray-600">
              Agent: {agents[deletingIndex]?.agentName}
              <br />
              This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <h3 className="font-semibold text-lg mb-2">Agents List</h3>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 border-b border-r text-left">Agent</th>
              <th className="px-4 py-2 border-b border-r text-left">
                Classification
              </th>
              {classification === "AGENT DOUBLE ENDER" && (
                <th className="px-4 py-2 border-b border-r text-left">
                  Transaction Type
                </th>
              )}
              <th className="px-4 py-2 border-b border-r text-left">Lead</th>
              <th className="px-4 py-2 border-b border-r text-left">Amount</th>
              <th className="px-4 py-2 border-b border-r text-left">Tax</th>
              <th className="px-4 py-2 border-b border-r text-left">Total</th>
              <th className="px-4 py-2 border-b border-r text-left">Fees</th>
              <th className="px-4 py-2 border-b border-r text-left">
                Office Exp.
              </th>
              <th className="px-4 py-2 border-b border-r text-left">Net</th>
              <th className="px-4 py-2 border-b border-r text-left">
                Buyer Rebate
              </th>
              <th className="px-4 py-2 border-b text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 ? (
              <tr>
                <td
                  colSpan={classification === "AGENT DOUBLE ENDER" ? 12 : 11}
                  className="text-center py-4 text-gray-500"
                >
                  No agents added yet.
                </td>
              </tr>
            ) : (
              agents.map((a, idx) => (
                <tr
                  key={idx}
                  className={
                    editingIndex === idx ? "bg-blue-50" : undefined
                  }
                >
                  <td className="px-4 py-2 border-b border-r">{a.agentName}</td>
                  <td className="px-4 py-2 border-b border-r">
                    {a.classification}
                  </td>
                  {classification === "AGENT DOUBLE ENDER" && (
                    <td className="px-4 py-2 border-b border-r">
                      {a.transactionType || "-"}
                    </td>
                  )}
                  <td className="px-4 py-2 border-b border-r">{a.lead}</td>
                  <td className="px-4 py-2 border-b border-r">{a.amount}</td>
                  <td className="px-4 py-2 border-b border-r">{a.tax}</td>
                  <td className="px-4 py-2 border-b border-r">{a.total}</td>
                  <td className="px-4 py-2 border-b border-r">
                    {a.feesDeducted}
                  </td>
                  <td className="px-4 py-2 border-b border-r">
                    {parseFloat(a.officeExpensesTotal || 0) > 0
                      ? a.officeExpensesTotal
                      : "-"}
                  </td>
                  <td className="px-4 py-2 border-b border-r">
                    {a.netCommission}
                  </td>
                  <td className="px-4 py-2 border-b border-r">
                    {a.buyerRebateIncluded === "yes"
                      ? `$${a.buyerRebateAmount}`
                      : "-"}
                  </td>
                  <td className="px-4 py-2 border-b whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => startEditAgent(idx)}
                      className="px-2 py-1 mr-2 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => startDeleteAgent(idx)}
                      className="px-2 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Fees Deduction Table */}
      <h3 className="font-semibold text-lg mb-2">Fees Deduction</h3>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 border-b border-r text-left">
                Fee Type
              </th>
              <th className="px-4 py-2 border-b border-r text-left">
                Deducted Fee
              </th>
              <th className="px-4 py-2 border-b text-left">Taxed</th>
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-4 text-gray-500">
                  No fees deduction entries yet.
                </td>
              </tr>
            ) : (
              agents.map((a, idx) => {
                const officeExp = computeOfficeExpenses(a, taxRate);
                return (
                  <React.Fragment key={`fee-deduction-${idx}`}>
                    {/* Fee Info Row */}
                    <tr>
                      <td className="px-4 py-2 border-b border-r">
                        {getPaymentPlanDisplayName(a.feeInfo)}
                      </td>
                      <td className="px-4 py-2 border-b border-r">
                        ${a.feesDeducted}
                      </td>
                      <td className="px-4 py-2 border-b">Yes</td>
                    </tr>
                    {/* Office expense rows (if any) */}
                    {officeExp.items.map((item) => (
                      <tr key={`${idx}-office-${item.key}`}>
                        <td className="px-4 py-2 border-b border-r">
                          {item.label}
                        </td>
                        <td className="px-4 py-2 border-b border-r">
                          ${roundTo2Str(item.amount)}
                        </td>
                        <td className="px-4 py-2 border-b">Yes</td>
                      </tr>
                    ))}
                    {/* Buyer Rebate Row (if exists) */}
                    {a.buyerRebateIncluded === "yes" && (
                      <tr>
                        <td className="px-4 py-2 border-b border-r">
                          Buyer Rebate
                        </td>
                        <td className="px-4 py-2 border-b border-r">
                          ${a.buyerRebateAmount}
                        </td>
                        <td className="px-4 py-2 border-b">No</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Save Button - Only show when not in edit mode */}
      {!isEditMode && (
        <div className="flex justify-end mt-8">
          <button
            type="button"
            className={`px-6 py-2 rounded font-semibold ${
              isSaving || isTradeSaved
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-blue-900 text-white hover:bg-blue-800"
            }`}
            onClick={handleSaveTradeClick}
            disabled={isSaving || isTradeSaved}
          >
            {isSaving
              ? "Saving..."
              : isTradeSaved
                ? "Trade Saved"
                : isAgent
                  ? "Save as Draft"
                  : "Save Trade"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentCommissionInfo;
